import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  FormColorVariant,
  PeriodOption,
  PopupMode,
  PopupState,
  StakingFormData,
  StakingPopupContextValue,
  StakingPopupVariant,
  TransactionStatus,
} from '../types';
import { useStakingContext } from './StakingDataContext';

const StakingPopupContext = createContext<StakingPopupContextValue | null>(null);

type StakingPopupProviderProps = {
  children: ReactNode;
};

const DEFAULT_ERROR_MESSAGES: Record<PopupMode, string> = {
  stake: 'Transaction failed',
  claim: 'Claim failed',
  withdraw: 'Withdrawal failed',
};

/**
 * Helper to build variant string based on mode and status type
 */
const buildVariant = (mode: PopupMode, status: 'confirm' | 'success' | 'error'): StakingPopupVariant => {
  if (mode === 'stake') return status;
  return `${mode}-${status}` as StakingPopupVariant;
};

/**
 * Helper to extract mode from variant
 */
const getModeFromVariant = (variant: StakingPopupVariant | null): PopupMode | null => {
  if (!variant) return null;
  if (variant === 'form' || variant === 'confirm' || variant === 'success' || variant === 'error') {
    return 'stake';
  }
  if (variant.startsWith('claim-')) return 'claim';
  if (variant.startsWith('withdraw-')) return 'withdraw';
  return null;
};

export const StakingPopupProvider = ({ children }: StakingPopupProviderProps) => {
  const [popupState, setPopupState] = useState<PopupState | null>(null);
  const [availablePeriods, setAvailablePeriods] = useState<PeriodOption[]>([]);

  const { transaction, stake, stakeVip, clearTransaction } = useStakingContext();

  // Derive current mode from popup state
  const currentMode = useMemo(() => getModeFromVariant(popupState?.variant ?? null), [popupState?.variant]);

  // Sync popup state with transaction status
  useEffect(() => {
    if (!popupState || !currentMode) return;

    const confirmVariant = buildVariant(currentMode, 'confirm');
    const successVariant = buildVariant(currentMode, 'success');
    const errorVariant = buildVariant(currentMode, 'error');

    switch (transaction.status) {
      case TransactionStatus.APPROVING:
      case TransactionStatus.PENDING:
        if (popupState.variant !== confirmVariant) {
          setPopupState((prev) => (prev ? { ...prev, variant: confirmVariant, txHash: null } : null));
        }
        break;

      case TransactionStatus.CONFIRMING:
        if (transaction.hash) {
          setPopupState((prev) => (prev ? { ...prev, variant: confirmVariant, txHash: transaction.hash } : null));
        }
        break;

      case TransactionStatus.SUCCESS:
        setPopupState((prev) => (prev ? { ...prev, variant: successVariant, txHash: transaction.hash } : null));
        break;

      case TransactionStatus.ERROR:
        setPopupState((prev) =>
          prev
            ? {
              ...prev,
              variant: errorVariant,
              errorMessage: transaction.error,
              // Use pendingAction for retry, or executeStake for stake mode
              onRetry: prev.pendingAction || (currentMode === 'stake' ? () => executeStake() : null),
            }
            : null
        );
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction.status, transaction.hash, transaction.error, currentMode]);

  const showForm = useCallback(
    (
      phaseName: string,
      phaseIndex: number,
      periods: PeriodOption[],
      isVip = false,
      colorVariant: FormColorVariant = 'default',
      selectedPeriodDays?: string
    ) => {
      clearTransaction();
      setAvailablePeriods(periods);

      const selectedPeriod = selectedPeriodDays ? periods.find((p) => p.days === selectedPeriodDays) : undefined;

      setPopupState({
        variant: 'form',
        formData: {
          phaseName,
          phaseIndex,
          periodDays: selectedPeriod?.days ?? '',
          periodLabel: selectedPeriod?.label ?? '',
          amount: '',
          expectedApy: selectedPeriod?.apyValue?.toString() ?? '',
          isVip,
          colorVariant,
        },
        txHash: null,
        errorMessage: null,
        onRetry: null,
        pendingAction: null,
      });
    },
    [clearTransaction]
  );

  const showConfirm = useCallback(
    (mode: PopupMode = 'stake', onRetry?: () => void) => {
      clearTransaction();
      setPopupState({
        variant: buildVariant(mode, 'confirm'),
        formData: null,
        txHash: null,
        errorMessage: null,
        onRetry: null,
        pendingAction: onRetry || null,
      });
    },
    [clearTransaction]
  );

  const showSuccess = useCallback((mode: PopupMode = 'stake', txHash?: string) => {
    setPopupState((prev) => (prev ? { ...prev, variant: buildVariant(mode, 'success'), txHash: txHash || prev.txHash } : null));
  }, []);

  const showError = useCallback((mode: PopupMode = 'stake', errorMessage?: string, onRetry?: () => void) => {
    setPopupState((prev) =>
      prev
        ? {
          ...prev,
          variant: buildVariant(mode, 'error'),
          errorMessage: errorMessage || DEFAULT_ERROR_MESSAGES[mode],
          onRetry: onRetry || null,
        }
        : null
    );
  }, []);

  const hide = useCallback(() => {
    clearTransaction();
    setPopupState(null);
    setAvailablePeriods([]);
  }, [clearTransaction]);

  const updateFormData = useCallback((data: Partial<StakingFormData>) => {
    setPopupState((prev) => {
      if (!prev || !prev.formData) return prev;
      return {
        ...prev,
        formData: { ...prev.formData, ...data },
      };
    });
  }, []);

  const executeStake = useCallback(async () => {
    if (!popupState?.formData) return;

    const { phaseIndex, periodDays, amount, expectedApy, isVip } = popupState.formData;

    if (!periodDays || !amount || !expectedApy) {
      console.error('Missing required form data for staking');
      return;
    }

    const stakeParams = {
      phaseIndex,
      periodDays,
      amount,
      expectedApy,
    };

    if (isVip) {
      await stakeVip(stakeParams);
    } else {
      await stake(stakeParams);
    }
  }, [popupState?.formData, stake, stakeVip]);

  const value: StakingPopupContextValue = {
    isOpen: popupState !== null,
    variant: popupState?.variant ?? null,
    formData: popupState?.formData ?? null,
    txHash: popupState?.txHash ?? null,
    errorMessage: popupState?.errorMessage ?? null,
    currentMode,
    onRetry: popupState?.onRetry ?? null,
    showForm,
    showConfirm,
    showSuccess,
    showError,
    hide,
    updateFormData,
    executeStake,
    availablePeriods,
  };

  return <StakingPopupContext.Provider value={value}>{children}</StakingPopupContext.Provider>;
};

export const useStakingPopup = (): StakingPopupContextValue => {
  const context = useContext(StakingPopupContext);
  if (!context) {
    throw new Error('useStakingPopup must be used within StakingPopupProvider');
  }
  return context;
};
